import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, HashRouter, Link, Route, Routes, useLocation } from 'react-router-dom';
import { AudioProvider, useAudio } from './audio';
import { I18nProvider, useI18n } from './i18n';
import { ThemeProvider, useTheme } from './theme';
import { DemonMark } from '../components/DemonMark';
import { useTvNavigation } from './tv-navigation';
import { OfflineState } from './OfflineState';
import { NativePlatformEffects } from './native-platform';
import Landing from '../routes/Landing';

const CreateRoom = lazy(() => import('../routes/CreateRoom'));
const JoinRoom = lazy(() => import('../routes/JoinRoom'));
const Room = lazy(() => import('../routes/Room'));
const HowToPlay = lazy(() => import('../routes/HowToPlay'));
const SharedDisplay = lazy(() => import('../routes/SharedDisplay'));
const Demo = lazy(() => import('../routes/Demo'));

function RouteMetadata() {
  const { pathname } = useLocation();
  const { text } = useI18n();
  useEffect(() => {
    const routeTitle = pathname.startsWith('/create')
      ? text('create.title')
      : pathname.startsWith('/join')
        ? text('join.title')
        : pathname.startsWith('/how-to-play')
          ? text('rules.title')
          : pathname.startsWith('/room') || pathname.startsWith('/screen') || pathname.startsWith('/demo')
            ? text('common.brand')
            : text('landing.tagline');
    document.title = `${routeTitle} · ${text('common.brand')}`;
    document.querySelector('meta[name="description"]')?.setAttribute('content', text('landing.description'));
  }, [pathname, text]);
  return null;
}

function AppHeader() {
  const { pathname } = useLocation();
  const { locale, setAppLocale, text } = useI18n();
  const { muted, setMuted } = useAudio();
  const { preference, setPreference } = useTheme();
  const gameplay = pathname.startsWith('/room/') || pathname.startsWith('/screen/') || pathname.startsWith('/demo');
  const nextTheme = preference === 'dark' ? 'light' : preference === 'light' ? 'system' : 'dark';
  return (
    <header className={`app-header${pathname === '/' ? ' app-header--landing' : ''}`}>
      <Link className="brand" to="/" aria-label={text('nav.home')} tabIndex={gameplay ? -1 : undefined}>
        <DemonMark className="brand__mark" />
        <span>{text('common.brand')}</span>
      </Link>
      {!gameplay && <div className="header-actions">
        <button type="button" className="header-pill" onClick={() => setAppLocale(locale === 'en' ? 'pt-PT' : 'en')} aria-label={text('nav.language')}>
          {locale === 'en' ? 'EN' : 'PT'}
        </button>
        <button type="button" className="icon-button" onClick={() => setPreference(nextTheme)} aria-label={`${text('nav.theme')}: ${text(preference === 'dark' ? 'nav.themeDark' : preference === 'light' ? 'nav.themeLight' : 'nav.themeSystem')}`}>
          <span aria-hidden="true">{preference === 'dark' ? '◐' : preference === 'light' ? '☀' : '◒'}</span>
        </button>
        <button type="button" className="icon-button" onClick={() => setMuted(!muted)} aria-pressed={muted} aria-label={text(muted ? 'nav.unmute' : 'nav.mute')}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10v4h4l5 4V6l-5 4H4Zm12.5-1.5a5 5 0 0 1 0 7M19 6a9 9 0 0 1 0 12" /><path className={muted ? '' : 'is-hidden'} d="m4 4 16 16" /></svg>
        </button>
      </div>}
    </header>
  );
}

function AppContent() {
  const { text } = useI18n();
  useTvNavigation();
  const Router = window.location.protocol === 'file:' ? HashRouter : BrowserRouter;
  return (
    <AudioProvider text={text}>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <RouteMetadata />
        <NativePlatformEffects />
        <OfflineState />
        <a className="skip-link" href="#main">{text('nav.skipToContent')}</a>
        <AppHeader />
        <main id="main">
          <Suspense fallback={<div className="loading-panel" role="status">{text('common.loading')}</div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/create" element={<CreateRoom />} />
              <Route path="/join" element={<JoinRoom />} />
              <Route path="/join/:roomCode" element={<JoinRoom />} />
              <Route path="/room/:roomCode" element={<Room />} />
              <Route path="/screen/:roomCode" element={<SharedDisplay />} />
              <Route path="/how-to-play" element={<HowToPlay />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="*" element={<Landing />} />
            </Routes>
          </Suspense>
        </main>
      </Router>
    </AudioProvider>
  );
}

export function App() {
  return <ThemeProvider><I18nProvider><AppContent /></I18nProvider></ThemeProvider>;
}
